import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import matter from 'gray-matter';
import { useThemeContext } from '../context/ThemeContext';
import {
  Server,
  Database,
  Layers,
  Cpu,
  Globe,
  Zap,
  Layout,
  Smartphone
} from 'lucide-react';
import { Buffer } from 'buffer';
import { AnimatedToken } from './components/Hld/AnimatedToken';
import { PulsingRing } from './components/Hld/PulsingRing';

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer;
}

const HldVisualizer = ({ content, metadata: passedMetadata, onNextChapter, nextChapterTitle, onMastered }) => {
  const { mode } = useThemeContext();
  const isLight = mode === 'light';
  const [hoveredNode, setHoveredNode] = useState(null);

  const nodeDescriptions = {
    client: "User-facing interface managing local state and user interactions.",
    server: "Business logic layer handling API requests and orchestrating services.",
    database: "Persistent storage for application data.",
    db: "Persistent storage for application data.",
    lb: "Distributes incoming traffic across instances.",
    cache: "High-speed data storage layer for fast access.",
    queue: "Asynchronous task processing and service decoupling."
  };

  const metadata = useMemo(() => {
    if (passedMetadata) return passedMetadata;
    try {
      const parsed = matter(content);
      return parsed.data;
    } catch {
      return null;
    }
  }, [content, passedMetadata]);

  const [step, setStep] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    setStep(0);
  }, [content]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const responsiveFactor = useMemo(() => {
    if (windowWidth > 2500) return 2.5;
    if (windowWidth > 2000) return 2.0;
    if (windowWidth > 1400) return 1.6;
    return 1.2;
  }, [windowWidth]);

  // --- RESIZING LOGIC ---
  const [panelWidth, setPanelWidth] = useState(240);
  const containerRef = useRef(null);
  const svgContainerRef = useRef(null);
  const isDragging = useRef(false);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.min(500, Math.max(200, e.clientX - rect.left));
      setPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Compute frame data before early return
  const frameData = useMemo(() => {
    if (!metadata || !metadata.frames || !metadata.nodes) return null;
    const { title = "System Architecture", nodes = [], edges = [], frames = [] } = metadata;
    const currentFrame = frames[step] || (frames.length > 0 ? frames[0] : {});
    const isComplete = step === frames.length - 1;
    const isStart = step === 0;
    return { title, nodes, edges, frames, currentFrame, isComplete, isStart };
  }, [metadata, step]);

  useEffect(() => {
    if (onMastered && frameData) onMastered(frameData.isComplete);
  }, [frameData?.isComplete, onMastered, frameData]);

  if (!metadata || !metadata.frames || !metadata.nodes) return null;

  const { title, nodes: rawNodes, edges: rawEdges, frames, currentFrame, isComplete, isStart } = frameData;
  const BASE_LAYOUT_SCALE = windowWidth > 2500 ? 2.2 : (windowWidth > 2000 ? 1.8 : 1.6);
  const LAYOUT_SCALE = BASE_LAYOUT_SCALE * responsiveFactor;

  // Scale coordinates to maintain spacing with larger boxes
  const nodes = rawNodes.map(n => ({ ...n, x: n.x * LAYOUT_SCALE, y: n.y * LAYOUT_SCALE }));
  const edges = rawEdges.map(e => ({ ...e, x: (e.x || 0) * LAYOUT_SCALE, y: (e.y || 0) * LAYOUT_SCALE }));

  const getNodeColor = (type) => {
    switch (type) {
      case 'client': return '#a855f7';
      case 'lb': return '#3b82f6';
      case 'database':
      case 'db': return '#10b981';
      case 'cache': return '#ef4444';
      case 'server': return '#1d4ed8';
      case 'queue': return '#f59e0b';
      default: return '#64748b';
    }
  };

  const getEdgePoint = (from, to, nodeW, nodeH, padding = 12) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dx === 0 && dy === 0) return to;
    const halfW = (nodeW / 2) + padding;
    const halfH = (nodeH / 2) + padding;
    const scaleX = Math.abs(halfW / dx);
    const scaleY = Math.abs(halfH / dy);
    const scale = Math.min(scaleX, scaleY);
    return { x: to.x - dx * scale, y: to.y - dy * scale };
  };

  const renderIcon = (type, color) => {
    const props = { size: 30, strokeWidth: 2.5, color };
    switch (type) {
      case 'client': return <Smartphone {...props} />;
      case 'server': return <Server {...props} />;
      case 'database':
      case 'db': return <Database {...props} />;
      case 'lb': return <Globe {...props} />;
      case 'cache': return <Layers {...props} />;
      case 'queue': return <Cpu {...props} />;
      default: return <Layout {...props} />;
    }
  };

  // Resolve token data from currentFrame
  const frameTokens = currentFrame.tokens || currentFrame.tokens || [];
  const nodeStates = currentFrame.node_states || currentFrame.node_states || {};

  return (
    <div
      style={{
        width: '100%',
        margin: '0 auto',
        background: isLight ? '#fdfdfd' : '#0a0a0c',
        borderRadius: '24px',
        border: isLight ? '1px solid #cbd5e1' : '1px solid #1f1f22',
        overflow: 'hidden',
        color: isLight ? '#1e293b' : '#fff',
        boxShadow: isLight ? '0 30px 60px rgba(0,0,0,0.06)' : '0 30px 60px rgba(0,0,0,0.7)',
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* DASHBOARD / CONTROLS */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 24px',
          background: isLight ? '#f8fafc' : '#18181b',
          borderBottom: `1px solid ${isLight ? '#e2e8f0' : '#27272a'}`,
          zIndex: 20
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => setStep(0)}
            style={{
              padding: '6px 14px',
              background: isLight ? '#fff' : '#27272a',
              color: isLight ? '#0f172a' : '#fff',
              borderRadius: '8px',
              border: `1px solid ${isLight ? '#e2e8f0' : '#27272a'}`,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
          >
            ↺ Reset
          </button>
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={isStart}
            style={{
              padding: '6px 14px',
              background: isLight ? '#fff' : '#27272a',
              color: isLight ? '#0f172a' : '#fff',
              borderRadius: '8px',
              border: `1px solid ${isLight ? '#e2e8f0' : '#27272a'}`,
              cursor: isStart ? 'default' : 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              opacity: isStart ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            ← Previous
          </button>
          <button
            onClick={() => setStep(s => Math.min(s + 1, frames.length - 1))}
            disabled={isComplete}
            style={{
              padding: '6px 20px',
              background: isComplete ? '#065f46' : '#2563eb',
              color: '#fff',
              borderRadius: '8px',
              border: 'none',
              cursor: isComplete ? 'default' : 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              opacity: isComplete ? 0.8 : 1,
              transition: 'all 0.3s ease'
            }}
          >
            {isComplete ? 'Finished ✓' : 'Next →'}
          </button>


          <div style={{ marginLeft: '12px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '140px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#a1a1aa', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase' }}>PROGRESS</span>
              <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#a1a1aa', fontWeight: 700 }}>Frame {step + 1} / {frames.length}</span>
            </div>
            <div style={{ height: '3px', background: isLight ? '#e2e8f0' : '#27272a', borderRadius: '2px', overflow: 'hidden' }}>
              <motion.div
                animate={{ width: `${((step + 1) / frames.length) * 100}%` }}
                transition={{ duration: 0.3 }}
                style={{ height: '100%', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)', borderRadius: '2px' }}
              />
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <span
            style={{
              fontWeight: 900, fontSize: '13px', letterSpacing: '1.5px',
              color: isLight ? '#0f172a' : '#f8fafc',
              textTransform: 'uppercase'
            }}
          >
            {title || "SYSTEM ARCHITECTURE"}
          </span>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div ref={containerRef} style={{ display: 'flex', height: '550px', background: isLight ? '#ffffff' : '#0f0f12', overflow: 'hidden' }}>
        {/* LEFT: Explanation Panel */}
        <div
          style={{
            width: `${panelWidth}px`,
            minWidth: '220px',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: isLight ? '#f8fafc' : '#141417',
            borderRight: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22',
            padding: '16px 20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Layout size={14} color="#3b82f6" />
            <span style={{ color: '#3b82f6', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '12px' }}>SYSTEM EXPLANATION</span>
          </div>

          <div style={{
            flex: 1,
            overflowY: 'auto',
            paddingRight: '8px',
            marginBottom: '12px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--scrollbar-thumb) var(--scrollbar-track)'
          }}>
            <div style={{ position: 'relative', marginBottom: '24px' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  style={{
                    lineHeight: 1.6,
                    color: isLight ? '#1e293b' : '#cbd5e1',
                    fontSize: '15.5px',
                    fontWeight: 500,
                    margin: 0,
                    width: '100%'
                  }}
                >
                  {currentFrame.explanation || currentFrame.msg}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* KEY TAKEAWAYS */}
            {currentFrame.takeaways && currentFrame.takeaways.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Zap size={14} color={isLight ? '#475569' : '#94a3b8'} style={{ opacity: 0.8 }} />
                  <span style={{ color: isLight ? '#475569' : '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', fontSize: '10px', opacity: 0.8 }}>KEY TAKEAWAYS</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {currentFrame.takeaways.map((point, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + i * 0.1 }}
                      style={{
                        fontSize: '13.5px',
                        color: isLight ? '#475569' : '#cbd5e1',
                        background: isLight ? '#fff' : 'rgba(245, 158, 11, 0.05)',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(245, 158, 11, 0.1)'}`,
                        lineHeight: '1.5',
                        fontWeight: 600,
                        display: 'flex',
                        gap: '10px'
                      }}
                    >
                      <span style={{ color: '#f59e0b', fontWeight: 900 }}>•</span>
                      {point}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* NODE STATE LEGEND */}
            {Object.keys(nodeStates).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  marginTop: '20px',
                  padding: '14px',
                  background: isLight ? '#f0fdf4' : 'rgba(16, 185, 129, 0.04)',
                  borderRadius: '10px',
                  border: `1px solid ${isLight ? '#bbf7d0' : 'rgba(16, 185, 129, 0.15)'}`
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 900, color: '#10b981', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
                  🔄 LIVE STATE
                </div>
                {Object.entries(nodeStates).map(([nodeId, state]) => {
                  const node = nodes.find(n => n.id === nodeId);
                  return (
                    <motion.div
                      key={nodeId}
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 0',
                        fontSize: '13px',
                        borderBottom: '1px dashed var(--border-main)'
                      }}
                    >
                      <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{node?.label || nodeId}</span>
                      <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '11px',
                        fontWeight: 800,
                        color: state.status?.includes('BAILOUT') ? '#ec4899' :
                          state.status?.includes('RENDER') ? '#f59e0b' :
                            state.status?.includes('FETCH') ? '#3b82f6' :
                              state.status?.includes('CLEAN') || state.status?.includes('NO') ? '#a1a1aa' : '#10b981',
                        background: 'var(--bg-glass)',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-main)'
                      }}>
                        {state.status}
                      </span>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </div>

          {/* EVENT STREAM */}
          <div style={{
            marginTop: 'auto',
            paddingTop: '20px',
            borderTop: '1px dashed var(--border-main)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff5f57' }} />
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffbd2e' }} />
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#27c93f' }} />
              <span style={{ fontSize: '10px', color: isLight ? '#64748b' : '#71717a', fontWeight: 900, letterSpacing: '1px', marginLeft: '4px' }}>EVENT STREAM</span>
            </div>
            <div style={{
              fontSize: '14px',
              color: isLight ? '#2563eb' : '#34d399',
              lineHeight: '1.4',
              fontFamily: "'JetBrains Mono', monospace",
              backgroundColor: 'var(--bg-default)',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border-main)',
              display: 'flex',
              gap: '8px'
            }}>
              <span style={{ opacity: 0.4 }}>❯</span>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 5 }}
                  transition={{ duration: 0.2 }}
                  style={{ flex: 1 }}
                >
                  {currentFrame.action || currentFrame.message?.text || currentFrame.msg || "System Idle..."}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Drag Handle */}
        <div
          onMouseDown={startResizing}
          style={{ width: '4px', cursor: 'col-resize', backgroundColor: 'transparent', transition: 'background-color 0.2s', zIndex: 10 }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3b82f6')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        />

        {/* RIGHT: SVG Architecture Diagram */}
        <div ref={svgContainerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--bg-default)' }}>
          {(() => {
            if (nodes.length === 0) return null;

            const xs = nodes.map(n => n.x);
            const ys = nodes.map(n => n.y);
            const minX = Math.min(...xs);
            const maxX = Math.max(...xs);
            const minY = Math.min(...ys);
            const maxY = Math.max(...ys);

            const paddingX = 100 * responsiveFactor;
            const paddingY = 80 * responsiveFactor;
            const boxMinX = minX - paddingX;
            const boxMaxX = maxX + paddingX;
            const boxMinY = minY - paddingY;
            const boxMaxY = maxY + paddingY;

            const boxWidth = boxMaxX - boxMinX;
            const boxHeight = boxMaxY - boxMinY;

            const viewWidth = Math.max(800, boxWidth);
            const viewHeight = Math.max(500, boxHeight);

            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;
            const viewX = centerX - viewWidth / 2;
            const viewY = centerY - viewHeight / 2;

            return (
              <svg
                width="100%"
                height="100%"
                viewBox={`${viewX} ${viewY} ${viewWidth} ${viewHeight}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <marker id="arrowhead-default" markerWidth="10" markerHeight="7" refX="35" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill={isLight ? "#cbd5e1" : "#334155"} />
                  </marker>
                  <marker id="arrowhead-active" markerWidth="10" markerHeight="7" refX="35" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
                  </marker>
                  <filter id="glow-filter" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                  <filter id="node-shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.15" />
                  </filter>

                  <linearGradient id="active-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                  <linearGradient id="edge-gradient-active" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1" />
                    <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                  </linearGradient>

                  <linearGradient id="light-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f8fafc" />
                    <stop offset="100%" stopColor="#f1f5f9" />
                  </linearGradient>

                  <pattern id="circuit-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1.5" fill={isLight ? "rgba(51, 65, 85, 0.15)" : "rgba(59, 130, 246, 0.05)"} />
                    <line x1="2" y1="2" x2="40" y2="2" stroke={isLight ? "rgba(51, 65, 85, 0.08)" : "rgba(59, 130, 246, 0.02)"} strokeWidth="1" />
                    <line x1="2" y1="2" x2="2" y2="40" stroke={isLight ? "rgba(51, 65, 85, 0.08)" : "rgba(59, 130, 246, 0.02)"} strokeWidth="1" />
                  </pattern>
                </defs>

                {/* Background Grid */}
                <rect
                  x={viewX} y={viewY} width={viewWidth} height={viewHeight}
                  fill={isLight ? "url(#light-grad)" : "url(#circuit-grid)"}
                />
                {isLight && (
                   <rect x={viewX} y={viewY} width={viewWidth} height={viewHeight} fill="url(#circuit-grid)" />
                )}

                {/* Atmospheric Ring */}
                <motion.circle
                  cx={centerX} cy={centerY} r={Math.min(viewWidth, viewHeight) / 2.5}
                  fill="transparent"
                  stroke={isLight ? "#e2e8f0" : "#1f1f22"}
                  strokeWidth="2"
                  strokeDasharray="10 10"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1, rotate: 360 }}
                  transition={{
                    scale: { duration: 1.5, ease: "easeOut" },
                    opacity: { duration: 1 },
                    rotate: { duration: 120, repeat: Infinity, ease: "linear" }
                  }}
                />

                {/* EDGES */}
                {edges.map((edge, idx) => {
                  const from = nodes.find(n => n.id === edge.from || n.id === edge.source);
                  const to = nodes.find(n => n.id === edge.to || n.id === edge.target);
                  if (!from || !to) return null;

                  // Skip if either end is hidden in the current frame
                  if (nodeStates[from.id]?.hidden || nodeStates[to.id]?.hidden) return null;

                  const isActive = currentFrame.activeEdges?.includes(edge.id) || currentFrame.active_edges?.includes(edge.id);

                  // Calculate intersection with target node boundary so arrowhead is visible
                  const nodeW = 230 * responsiveFactor;
                  const nodeH = 120 * responsiveFactor;
                  const edgeEnd = getEdgePoint(from, to, nodeW, nodeH, 10 * responsiveFactor);

                  return (
                    <g key={edge.id}>
                      {/* Animated dashes background */}
                      <motion.line
                        x1={from.x} y1={from.y} x2={edgeEnd.x} y2={edgeEnd.y}
                        stroke={isActive ? "#3b82f6" : (isLight ? "#cbd5e1" : "#27272a")}
                        strokeWidth={isActive ? 8 : 3}
                        strokeOpacity={isActive ? 0.2 : (isLight ? 0.3 : 0.45)}
                        strokeDasharray={isActive ? "5 5" : "0"}
                        animate={isActive ? { strokeDashoffset: [-20, 0] } : {}}
                        transition={isActive ? { duration: 1, repeat: Infinity, ease: "linear" } : {}}
                      />
                      {/* Main edge line */}
                      <motion.line
                        x1={from.x} y1={from.y} x2={edgeEnd.x} y2={edgeEnd.y}
                        stroke={isActive ? "#3b82f6" : (isLight ? "#94a3b8" : "#404040")}
                        strokeWidth={isActive ? 3 : 1.5}
                        markerEnd={isActive ? "url(#arrowhead-active)" : "url(#arrowhead-default)"}
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{
                          pathLength: 1,
                          opacity: isActive ? 1 : 0.7
                        }}
                        transition={{
                          pathLength: { duration: 1.2, delay: idx * 0.04, ease: "easeInOut" },
                          opacity: { duration: 0.6 }
                        }}
                      />
                    </g>
                  );
                  })}

                  {/* NODES - Pass 1: Bodies */}
                {nodes.map((node, idx) => {
                  const nodeState = nodeStates[node.id];
                  if (nodeState?.hidden) return null;

                  const isActive = (currentFrame.activeNodes?.includes(node.id) || currentFrame.active_nodes?.includes(node.id)) ?? false;
                  const color = getNodeColor(node.type);
                  const isHovered = hoveredNode === node.id;

                  return (
                    <motion.g
                      key={`body-${node.id}`}
                      initial={{ opacity: 0, scale: 0.5, y: 50 }}
                      animate={{
                        opacity: 1,
                        scale: isActive ? 1.15 : (isHovered ? 1.05 : 1),
                        y: isActive || isHovered ? 0 : [0, -5, 0],
                        filter: isActive ? 'drop-shadow(0 0 15px rgba(59, 130, 246, 0.5))' : 'none'
                      }}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                      transition={{
                        opacity: { delay: 0.5 + idx * 0.15, duration: 0.5 },
                        scale: { delay: 0.5 + idx * 0.15, type: "spring", stiffness: 260, damping: 20 },
                        y: isActive || isHovered
                          ? { type: "spring", stiffness: 300, damping: 20 }
                          : { delay: 0.5 + idx * 0.15, duration: 4, repeat: Infinity, ease: "easeInOut" }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <g transform={`translate(${node.x}, ${node.y})`}>
                        {/* Shadow/Glow */}
                        {(isLight || isActive || isHovered) && (
                          <rect
                            x={-115 * responsiveFactor} y={-60 * responsiveFactor} 
                            width={230 * responsiveFactor} height={120 * responsiveFactor} 
                            rx={18 * responsiveFactor}
                            fill={isActive ? color : (isHovered ? color : (isLight ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.05)"))}
                            style={{
                              transform: `translate(0px, ${5 * responsiveFactor}px)`,
                              filter: (isActive || isHovered) ? `blur(${18 * responsiveFactor}px) opacity(${isLight ? 0.3 : 0.6})` : `blur(${10 * responsiveFactor}px)`,
                              opacity: (isActive || isHovered) ? (isLight ? 0.4 : 0.7) : 1
                            }}
                          />
                        )}

                        {/* Main Cell Body */}
                        <motion.rect
                          x={-115 * responsiveFactor} y={-60 * responsiveFactor} 
                          width={230 * responsiveFactor} height={120 * responsiveFactor} 
                          rx={18 * responsiveFactor}
                          fill={isActive ? (isLight ? "#fff" : "#18181b") : (isLight ? "#ffffff" : "#18181b")}
                          stroke={isActive ? color : (isHovered ? color : (isLight ? "#e2e8f0" : "#27272a"))}
                          strokeWidth={isActive ? 4 * responsiveFactor : (isHovered ? 3 * responsiveFactor : 2 * responsiveFactor)}
                          style={{
                            filter: isActive ? 'url(#glow-filter)' : (isHovered ? 'url(#node-shadow)' : 'none'),
                            backdropFilter: `blur(${8 * responsiveFactor}px)`,
                            boxShadow: isLight && (isActive || isHovered) ? `0 8px 24px ${color}15` : 'none'
                          }}
                        />

                        {/* Header Bar */}
                        <path
                          d={`M ${-114 * responsiveFactor} ${-40 * responsiveFactor} Q ${-114 * responsiveFactor} ${-59 * responsiveFactor} ${-100 * responsiveFactor} ${-59 * responsiveFactor} L ${100 * responsiveFactor} ${-59 * responsiveFactor} Q ${114 * responsiveFactor} ${-59 * responsiveFactor} ${114 * responsiveFactor} ${-40 * responsiveFactor} L ${114 * responsiveFactor} ${-15 * responsiveFactor} L ${-114 * responsiveFactor} ${-15 * responsiveFactor} Z`}
                          fill={isActive ? "url(#active-gradient)" : (isHovered ? color : (isLight ? "#f1f5f9" : "#27272a"))}
                          stroke={isLight && !isActive && !isHovered ? "#e2e8f0" : "none"}
                        />

                        <text x="0" y={-44 * responsiveFactor} fill={isActive ? "#fff" : (isLight && isHovered ? "#1e293b" : (isHovered ? "#fff" : (isLight ? "#475569" : "#94a3b8")))} fontSize={13 * responsiveFactor} fontWeight="1000" textAnchor="middle" style={{ textTransform: 'uppercase', letterSpacing: `${2 * responsiveFactor}px` }}>
                          {node.type}
                        </text>

                        <motion.g
                          transform={`translate(${-15 * responsiveFactor}, ${-10 * responsiveFactor})`}
                          animate={isActive ? { y: [-2 * responsiveFactor, -4 * responsiveFactor, -2 * responsiveFactor] } : {}}
                          transition={{ repeat: Infinity, duration: 2 }}
                        >
                          {renderIcon(node.type, isActive ? "#fff" : (isLight && isHovered ? "#1e293b" : (isHovered ? "#fff" : (isLight ? "#334155" : "#475569"))))}
                        </motion.g>

                        {/* Label */}
                        <foreignObject 
                          x={-110 * responsiveFactor} 
                          y={25 * responsiveFactor} 
                          width={220 * responsiveFactor} 
                          height={40 * responsiveFactor}
                        >
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            color: isActive ? (isLight ? '#1e293b' : '#fff') : (isLight ? '#475569' : '#cbd5e1'),
                            fontSize: `${16 * responsiveFactor}px`,
                            fontWeight: 900,
                            lineHeight: '1.2',
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            textShadow: isActive && isLight ? `0 ${1 * responsiveFactor}px ${3 * responsiveFactor}px rgba(0,0,0,0.1)` : 'none'
                          }}>
                            {node.label}
                          </div>
                        </foreignObject>
                      </g>
                    </motion.g>
                  );
                })}

                {/* NODES - Pass 2: Badges (Rendered after all bodies to be on top) */}
                {nodes.map((node, idx) => {
                  const nodeState = nodeStates[node.id];
                  if (!nodeState || nodeState.hidden) return null;

                  const isActive = (currentFrame.activeNodes?.includes(node.id) || currentFrame.active_nodes?.includes(node.id)) ?? false;
                  const isHovered = hoveredNode === node.id;

                  return (
                    <motion.g
                      key={`badge-${node.id}`}
                      initial={{ opacity: 0, scale: 0.5, y: 50 }}
                      animate={{
                        opacity: 1,
                        scale: isActive ? 1.15 : (isHovered ? 1.05 : 1),
                        y: isActive || isHovered ? node.y : [node.y, node.y - 5, node.y],
                      }}
                      transition={{
                        opacity: { delay: 0.7 + idx * 0.1, duration: 0.5 },
                        scale: { delay: 0.7 + idx * 0.1, type: "spring", stiffness: 260, damping: 20 },
                        y: isActive || isHovered
                          ? { type: "spring", stiffness: 300, damping: 20 }
                          : { delay: 0.7 + idx * 0.1, duration: 4, repeat: Infinity, ease: "easeInOut" }
                      }}
                      style={{ pointerEvents: 'none' }}
                    >
                      <g transform={`translate(${node.x}, 0)`}>
                        <AnimatePresence>
                          <motion.g
                             key={`state-${node.id}-${step}`}
                             initial={{ opacity: 0, y: 15 }}
                             animate={{ opacity: 1, y: 0 }}
                             exit={{ opacity: 0, y: -10 }}
                             transition={{ duration: 0.4, delay: 0.2 }}
                          >
                            <rect
                              x={-100 * responsiveFactor} y={-85 * responsiveFactor} 
                              width={200 * responsiveFactor} height={34 * responsiveFactor} 
                              rx={17 * responsiveFactor}
                              fill={
                                nodeState.status?.includes('BAILOUT') ? (isLight ? '#fce7f3' : '#ec489970') :
                                  nodeState.status?.includes('RENDER') ? (isLight ? '#fef3c7' : '#f59e0b70') :
                                    nodeState.status?.includes('FETCH') ? (isLight ? '#dbeafe' : '#3b82f670') :
                                      nodeState.status?.includes('STATE') ? (isLight ? '#d1fae5' : '#10b98180') :
                                        (isLight ? '#f1f5f9' : '#1e1e24')
                              }
                              stroke={
                                nodeState.status?.includes('BAILOUT') ? '#ec4899' :
                                  nodeState.status?.includes('RENDER') ? '#f59e0b' :
                                    nodeState.status?.includes('FETCH') ? '#3b82f6' :
                                      nodeState.status?.includes('STATE') ? '#10b981' :
                                        (isLight ? '#cbd5e1' : '#334155')
                              }
                              strokeWidth={2.5 * responsiveFactor}
                            />
                            <text
                              y={-60 * responsiveFactor} textAnchor="middle"
                              fill={
                                nodeState.status.includes('BAILOUT') ? (isLight ? '#9d174d' : '#ff85c0') :
                                  nodeState.status.includes('RENDER') ? (isLight ? '#92400e' : '#ffb347') :
                                    nodeState.status.includes('FETCH') ? (isLight ? '#1e40af' : '#60a5fa') :
                                      nodeState.status.includes('STATE') ? (isLight ? '#065f46' : '#34d399') :
                                        (isLight ? '#334155' : '#ffffff')
                              }
                              fontSize={14 * responsiveFactor} fontWeight="1000" letterSpacing="0.8px"
                              style={{ textShadow: !isLight ? `0 ${2 * responsiveFactor}px ${8 * responsiveFactor}px rgba(0,0,0,1)` : 'none' }}
                            >
                              {nodeState.status}
                            </text>
                          </motion.g>
                        </AnimatePresence>
                      </g>
                    </motion.g>
                  );
                })}

                {/* ANIMATED TOKENS */}
                <AnimatePresence>
                  {frameTokens.map((token, ti) => {
                    const source = token.source_node ? nodes.find(n => n.id === token.source_node) : null;
                    const target = token.target_node ? nodes.find(n => n.id === token.target_node) : null;
                    const color = token.color || '#3b82f6';

                    if (source && target) {
                      return (
                        <AnimatedToken
                          key={`token-${step}-${token.id}`}
                          from={{ ...source, x: source.x, y: source.y }}
                          to={{ ...target, x: target.x, y: target.y }}
                          color={color}
                          label={token.label}
                          delay={ti * 0.2}
                          scale={responsiveFactor}
                        />
                      );
                    } else if (target) {
                      return (
                        <PulsingRing
                          key={`pulse-${step}-${token.id}`}
                          x={target.x}
                          y={target.y}
                          color={color}
                        />
                      );
                    }
                    return null;
                  })}

                  {/* EDGE-BASED DATA PACKETS (active edges without explicit tokens) */}
                  {frameTokens.length === 0 && (currentFrame.activeEdges || currentFrame.active_edges || []).map((edgeId) => {
                    const edge = edges.find(e => e.id === edgeId);
                    if (!edge) return null;
                    const from = nodes.find(n => n.id === edge.from || n.id === edge.source);
                    const to = nodes.find(n => n.id === edge.to || n.id === edge.target);
                    if (!from || !to) return null;

                    return (
                      <AnimatedToken
                        key={`flow-${edgeId}-${step}`}
                        from={from}
                        to={to}
                        color={getNodeColor(from.type)}
                        scale={responsiveFactor}
                      />
                    );
                  })}
                </AnimatePresence>
              </svg>
            );
          })()}
        </div>

        {/* PORTAL TOOLTIP — rendered at document.body so no overflow can clip it */}
        {typeof document !== 'undefined' && ReactDOM.createPortal(
          <AnimatePresence>
            {hoveredNode && (() => {
              const node = nodes.find(n => n.id === hoveredNode);
              if (!node || !svgContainerRef.current) return null;

              // Get the container's bounding rect to map SVG coords → viewport coords
              const containerRect = svgContainerRef.current.getBoundingClientRect();

              const xs = nodes.map(n => n.x);
              const ys = nodes.map(n => n.y);
              const minX = Math.min(...xs);
              const maxX = Math.max(...xs);
              const minY = Math.min(...ys);
              const maxY = Math.max(...ys);
              const viewWidth = Math.max(800, (maxX - minX) + 400);
              const viewHeight = Math.max(500, (maxY - minY) + 300);
              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;
              const viewX = centerX - viewWidth / 2;
              const viewY = centerY - viewHeight / 2;

              // Map SVG coordinate to pixel position within the container
              const pctX = (node.x - viewX) / viewWidth;
              const pctY = (node.y - viewY) / viewHeight;

              // SVG preserveAspectRatio="xMidYMid meet" — compute actual rendered area
              const containerW = containerRect.width;
              const containerH = containerRect.height;
              const svgAspect = viewWidth / viewHeight;
              const containerAspect = containerW / containerH;

              let renderedW, renderedH, offsetX, offsetY;
              if (containerAspect > svgAspect) {
                // Container is wider than SVG — SVG is height-limited
                renderedH = containerH;
                renderedW = containerH * svgAspect;
                offsetX = (containerW - renderedW) / 2;
                offsetY = 0;
              } else {
                // Container is taller than SVG — SVG is width-limited
                renderedW = containerW;
                renderedH = containerW / svgAspect;
                offsetX = 0;
                offsetY = (containerH - renderedH) / 2;
              }

              const pixelX = containerRect.left + offsetX + pctX * renderedW;
              const pixelY = containerRect.top + offsetY + pctY * renderedH;

              // Clamp to stay within viewport
              const tooltipWidth = 210;
              const clampedLeft = Math.min(Math.max(pixelX - tooltipWidth / 2, 8), window.innerWidth - tooltipWidth - 8);
              const clampedTop = Math.min(pixelY + 50, window.innerHeight - 100);

              return (
                <motion.div
                  key={`tooltip-${node.id}`}
                  initial={{ opacity: 0, scale: 0.85, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.85, y: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  style={{
                    position: 'fixed',
                    left: `${clampedLeft}px`,
                    top: `${clampedTop}px`,
                    zIndex: 99999,
                    pointerEvents: 'none',
                    width: `${tooltipWidth}px`
                  }}
                >
                  <div style={{
                    background: isLight ? 'rgba(37, 99, 235, 0.95)' : 'rgba(30, 41, 59, 0.95)',
                    color: '#fff',
                    padding: '12px 16px',
                    borderRadius: '14px',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    textAlign: 'center',
                    boxShadow: isLight
                      ? '0 12px 30px -5px rgba(37, 99, 235, 0.35)'
                      : '0 12px 30px -5px rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${isLight ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)'}`,
                    lineHeight: '1.45',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '14px',
                      background: 'radial-gradient(circle at top left, rgba(255,255,255,0.2) 0%, transparent 60%)',
                      pointerEvents: 'none'
                    }} />
                    <div style={{
                      position: 'absolute',
                      top: '-6px',
                      left: '50%',
                      transform: 'translateX(-50%) rotate(45deg)',
                      width: '12px',
                      height: '12px',
                      background: isLight ? 'rgba(37, 99, 235, 0.95)' : 'rgba(30, 41, 59, 0.95)',
                      borderTop: `1px solid ${isLight ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)'}`,
                      borderLeft: `1px solid ${isLight ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)'}`,
                    }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <div style={{ fontSize: '9px', fontWeight: 900, textTransform: 'uppercase', marginBottom: '5px', opacity: 0.8, letterSpacing: '1px' }}>COMPONENT ROLE</div>
                      {nodeDescriptions[node.type] || "Architectural Component"}
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </div>
  );
};

export default HldVisualizer;